/**
* UI Service
*/
const uiService = ($filter, $state, jqService, $timeout) => {
  /**
  * Contains the datetime formats allowed across the application.
  *
  * moment.js reference:
  *  formats: http://momentjs.com/docs/#/parsing/string-format/
  *  format(): http://momentjs.com/docs/#/displaying/format/
  */
  const _dateFormats = {
    date: 'MM/DD/YYYY',
    dateTimeSimple: 'MM/DD/YYYY HH:mm',
    dateTimeAt: 'MM/DD/YYYY [at] HH:mm',
    dateTime12h: 'MM/DD/YYYY hh:mm A',
    dateTimeISO: 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]',
    hour: 'HH:mm'
  };

  /**
  * Constant: regular expressions to be used in forms.
  */
  const _regex = {
    email: '^([a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5})$',
    number: '^[0-9]*$',
    // Zipcode for US-Canada standards
    zipcode: '^[0-9]{5}(?:-[0-9]{4})?$',
    // Phone should only allow numbers and '+' at the beginning to avoid inconsistency
    phone: '^[0-9\-\+]{9,15}$',
    name: '^[A-Za-z][A-Za-z]*(?:\s+[A-Za-z][A-Za-z]+)?$',
    noNumbers: '^[A-Za-z_ ][A-Za-z_ ]*(?:\s+[A-Za-z_ ][A-Za-z_ ]+)?$',
    address: '[a-zA-Z0-9À-ÖØ-öø-ÿ]+\.?(( |\-)[a-zA-Z0-9À-ÖØ-öø-ÿ]+\.?)*',
    // Format for currency (dots and commas are optional): #,###.##
    currency: '(?=.)^(([1-9][0-9]{0,2}(,[0-9]{3})*)|[0-9]+)?(\\.[0-9]{1,2})?$',
    // Matches if string has these special characters: `~!@#$%^&*()_|+\=?<>{}[]/\
    specialCharacters: /[`~!@#$%^&*()_|+=?<>\{\}\[\]\\\/]/,
    // Matches if string does NOT have these special characters: `~!@#$%^&*()_|+\=?<>{}[]/\
    noSpecialCharacters: /^(?!.*[`~!@#$%^&*()_|+=?<>\{\}\[\]\\\/])/,
    password: '(?=(.*[0-9]))(?=.*[a-z])(?=(.*[A-Z]))(?=.*[\!@#$^&*!])(?=(.*)).{8,160}',
    website: /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/
  };

  /**
  * assignBindObject function: assigns all attributes from second parameter as attributes to first
  * parameter, this is for bind objects so it wont lose the reference by direct assignation
  * @param objectTo {Object} destination object
  * @param objectFrom {Object} source object
  */
  const assignBindObject = (objectTo, objectFrom) => {
    // First cleans all destination object's attributes
    Object.keys(objectTo).forEach((key) => {
      delete objectTo[key];
    });
    // Assigns each one of the source attributes to the destination object
    Object.keys(objectFrom).forEach((key) => {
      objectTo[key] = objectFrom[key];
    });
  };

  /**
  * This function receives a module object as param, then reassigns selectedModule object with this param
  * then redirects to the right route state with the new selectedModule.name
  *
  * Used in: adminModule.store and entityMenuItem.controller.
  *
  * @param selectedModule {Object} Reference of 'selectedModule' variable used across components.
  * @param selected {Object} Current option selected from the menu.
  */
  const setActiveEntity = (selectedModule, selected) => {
    // Assigns select object attributes one by one to bind object this.selectedModule
    assignBindObject(selectedModule, selected);
    // Changes state to selected module
    $state.go(selectedModule.path);
  };

  /**
  * Returns random alpha-numeric string.
  */
  const getRandom = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
  };

  /**
  * Check if value passes the regex test.
  * @param value {String} Value to test.
  * @param regexName {String} Mandatory. Regex name to use for the testing.
  * @returns {boolean}
  */
  const checkValueWithRegex = (value = '', regexName) => {
    let result = false;

    if (regexName && _regex[regexName]) {
      result = new RegExp(_regex[regexName]).test(value.toString());
    }

    return result;
  };

  /**
  * The object
  */
  return Object.freeze({
    dateFormats: _dateFormats,
    regex: _regex,
    /**
    * isFirstItem: get the $first property from scope parent, used when directive is
    * loaded from a ng-repeat loop, and its needed to know if this item is the first,
    * used for ng-class assigning purposes.
    * @returns {Boolean}
    * @param scope {Object} scope variable received to check
    */
    isFirstItem: (scope) => {
      return (scope.$parent && scope.$parent.$first) ? scope.$parent.$first : false;
    },
    /**
    * isLastItem: get the $last property from scope parent, used when directive is
    * loaded from a ng-repeat loop, and its needed to know if this item is the last,
    * used for ng-class assigning purposes.
    * @returns {Boolean}
    * @param scope {Object} scope variable received to check
    */
    isLastItem: (scope) => {
      return (scope.$parent && scope.$parent.$last) ? scope.$parent.$last : false;
    },
    assignBindObject: assignBindObject,
    setActiveEntity: setActiveEntity,
    checkValueWithRegex: checkValueWithRegex,
    /**
    *
    * @param stringValue
    * @returns {string}
    */
    capitalizeFirstLetter: (stringValue = '') => {
      return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
    },
    /**
    * Generates an unbound copy of 'objectArray' (Ex: to lose the reference between the store/model data).
    * @param objectArray {Array} array of objects
    * @returns {*|Array} unbound copy of 'objectArray'
    */
    unboundObjectArray: (objectArray = []) => {
      return objectArray.map(item => {
        // Returns a copy of 'item' to lose the reference of the original model data.
        return JSON.parse(JSON.stringify(item));
      });
    },
    /**
    * Generates unique ID, format: xxxxx-xxxxx-xxxxx
    */
    getUniqueID: () => {
      return `${getRandom()}-${getRandom()}-${getRandom()}`;
    },

    showCarusel: () => {
        $timeout(function(){
            jqService("#imageslider").bose({
                images : ['images/imageslider/slider-01.jpg',
                    'images/imageslider/slider-02.jpg',
                    'images/imageslider/slider-03.jpg',
                    'images/imageslider/slider-04.jpg'
                ],
                imageTitles  : [
                    ['Slide Image 1', 'Waste Management'],
                    ['Slide Image 2', 'Hire your skip bin online'],
                    ['Slide Image 3', 'Reliable and competitively priced skip bin hire services'],
                    ['Slide Image 4', 'Conventional skip bin']
                ],
                wrapClass    : 'bose-wrapper',
                sliderClass  : 'bose-sliders ',
                holderClass  : 'bose-holder',
                startIndex   : 0,
                transition   : 'fade',
                timeout      : 5, // 5 sec
                duration     : 2 // 2 sec
            });
        });
    },

    showMenu: (close) => {
        if (close || jqService("nav#nav-mobile ul").hasClass("expanded")) {
            jqService("nav#nav-mobile ul.expanded").removeClass("expanded").slideUp(250);
            jqService(this).removeClass("open");
        } else {
            jqService("nav#nav-mobile ul").addClass("expanded").slideDown(250);
            jqService(this).addClass("open");
        }
    }

  });
};

uiService.$inject = ['$filter', '$state', 'jqService', '$timeout'];
export {uiService};



/** WEBPACK FOOTER **
 ** ./src/app/shared/services/ui.service.js
 **/